const INDIVIDUAL_FIELDS = ['civility', 'first_name', 'last_name', 'birth_date']
const COMPANY_FIELDS = ['company_name', 'tva', 'business_number', 'siret']

export type PersonLikeRecord = Record<string, unknown>

/**
 * Values a type switch cleared, kept by the caller for the lifetime of the
 * form so that switching back restores them instead of losing them.
 */
export type ClearedPersonTypeValues = Record<string, unknown>

export interface ApplyPersonTypeOptions {
  /**
   * False keeps the civility and names when switching to a company: they then
   * identify the contact person at that company.
   */
  clearPersonFields?: boolean
  /**
   * Mutated in place: receives the non-empty values this switch clears, and
   * gives back the ones of the type switched to.
   */
  clearedValues?: ClearedPersonTypeValues
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}

/**
 * Return a copy of a person-like record switched to the given type, with the
 * fields that only make sense for the other type cleared.
 *
 * Switching to an individual always clears the company fields. Switching to a
 * company clears the individual fields unless `clearPersonFields` is false.
 *
 * With `clearedValues`, a round trip (individual → company → individual) puts
 * back what the first switch cleared, so a misclick on an existing record does
 * not wipe its saved names or birth date.
 */
export function applyPersonType<T extends PersonLikeRecord>(
  record: T,
  isCompany: boolean,
  { clearPersonFields = true, clearedValues }: ApplyPersonTypeOptions = {}
): T & { is_company: boolean } {
  const next: PersonLikeRecord = { ...record, is_company: isCompany }

  let staleFields = COMPANY_FIELDS
  if (isCompany) {
    staleFields = clearPersonFields ? INDIVIDUAL_FIELDS : []
  }
  const restoredFields = isCompany ? COMPANY_FIELDS : INDIVIDUAL_FIELDS

  if (clearedValues) {
    restoredFields.forEach((field) => {
      if (field in clearedValues) {
        if (isEmpty(next[field])) {
          next[field] = clearedValues[field]
        }
        delete clearedValues[field]
      }
    })
  }

  staleFields.forEach((field) => {
    if (clearedValues && !isEmpty(next[field])) {
      clearedValues[field] = next[field]
    }
    next[field] = null
  })

  return next as T & { is_company: boolean }
}
